<form [formGroup]="tankProfileForm" class="relative block h-full">
  <div *ngIf="loadingProfile" class="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
    <mat-spinner diameter="50"></mat-spinner>
  </div>

  <div *ngIf="currentTankProfile != null" class="profile-layout grid grid-cols-1 border-x-0 border-y-0 border-t border-solid border-pill">
    <!-- Columns -->
    <section class="column-panel flex min-w-0 flex-col gap-2 py-6">
      <div class="flex items-baseline justify-between gap-2">
        <h4 class="m-0 text-sm font-semibold">{{ 'TankProfiles.ManageColumns' | translate }}</h4>
        <span class="text-xs text-gray-500">{{
          'TankProfiles.SelectedCount' | translate: { selected: selectedColumnCount, total: tankGridColumns.length }
        }}</span>
      </div>

      <mat-form-field appearance="outline" class="tl-labelless-field tl-labelless-field--flush w-full">
        <mat-icon matPrefix class="tl-icon-sm text-gray-500">search</mat-icon>
        <input
          matInput
          type="text"
          [value]="columnFilter"
          (input)="onColumnFilterChange($any($event.target).value)"
          [placeholder]="'TankProfiles.FilterColumns' | translate"
          [attr.aria-label]="'TankProfiles.FilterColumns' | translate"
        />
        <button
          *ngIf="isFilteringColumns"
          matSuffix
          mat-icon-button
          type="button"
          (click)="clearColumnFilter()"
          [attr.aria-label]="'TankProfiles.ClearFilter' | translate"
        >
          <mat-icon class="tl-icon-sm">close</mat-icon>
        </button>
      </mat-form-field>

      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <button
          type="button"
          class="cursor-pointer border-0 bg-transparent p-0 font-sans text-sm text-theme-700 hover:underline disabled:cursor-default disabled:text-gray-400 disabled:no-underline"
          [disabled]="readOnly"
          (click)="selectAllColumns()"
        >
          {{ 'TankProfiles.SelectAll' | translate }}
        </button>
        <button
          type="button"
          class="cursor-pointer border-0 bg-transparent p-0 font-sans text-sm text-theme-700 hover:underline disabled:cursor-default disabled:text-gray-400 disabled:no-underline"
          [disabled]="readOnly"
          (click)="clearOptionalColumns()"
        >
          {{ 'TankProfiles.ClearOptional' | translate }}
        </button>
        <span *ngIf="!readOnly" class="text-xs text-gray-500" aria-live="polite">{{
          (isFilteringColumns ? 'TankProfiles.ReorderDisabledWhileFiltering' : 'TankProfiles.DragToReorder') | translate
        }}</span>
      </div>

      <div
        class="column-list max-h-96 min-h-0 overflow-y-auto rounded border-x border-y border-solid border-pill"
        role="list"
        cdkDropList
        [cdkDropListDisabled]="readOnly || isFilteringColumns"
        (cdkDropListDropped)="drop($event)"
      >
        <div
          *ngFor="let column of visibleColumns; trackBy: trackColumn"
          cdkDrag
          role="listitem"
          class="column-row flex items-center gap-2 border-x-0 border-y-0 border-b border-solid border-pill/50 bg-white px-2 py-1 last:border-b-0"
        >
          <mat-icon
            cdkDragHandle
            class="tl-icon-sm shrink-0 text-gray-400"
            [ngClass]="readOnly || isFilteringColumns ? 'cursor-default opacity-40' : 'cursor-grab'"
            aria-hidden="true"
          >drag_indicator</mat-icon>
          <mat-checkbox
            *ngIf="column.required"
            color="primary"
            class="tl-inline-checkbox min-w-0"
            [checked]="true"
            [disabled]="true"
          >{{ columnLabel(column) }}</mat-checkbox>
          <mat-checkbox
            *ngIf="!column.required"
            color="primary"
            class="tl-inline-checkbox min-w-0"
            [(ngModel)]="column.isSelected"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="readOnly"
            (ngModelChange)="markDirty()"
          >{{ columnLabel(column) }}</mat-checkbox>
          <mat-icon
            *ngIf="showPreTelemetryInfoIcon(column.databaseName)"
            class="tl-icon-sm shrink-0 cursor-help text-gray-500"
            [matTooltip]="preTelemetryInfoText"
            tabindex="0"
            [attr.aria-label]="preTelemetryInfoText"
          >info</mat-icon>
          <span *ngIf="column.required" class="ml-auto shrink-0 text-xs text-gray-500">{{
            'TankProfiles.RequiredColumn' | translate
          }}</span>
        </div>
        <p *ngIf="visibleColumns.length === 0" class="m-0 p-6 text-center text-sm text-gray-500">
          {{ 'TankProfiles.NoColumnsMatchFilter' | translate }}
        </p>
      </div>
    </section>

    <!-- Settings -->
    <div class="settings-panel flex min-w-0 flex-col pr-2.5 border-x-0 border-y-0 border-t border-solid border-pill">
      <section class="flex flex-col gap-3 border-x-0 border-y-0 border-b border-solid border-pill py-6">
        <h4 class="m-0 text-sm font-semibold">{{ 'TankProfiles.Sorting' | translate }}</h4>

        <ng-container
          *ngTemplateOutlet="
            sortRow;
            context: {
              which: 'primary',
              label: 'TankProfiles.SortFirst',
              ariaLabel: 'TankProfiles.PrimarySort',
              columnControl: 'primaryColumnId',
              directionControl: 'primaryColumnSortTypeId',
              asc: PRIMARY_SORT_ASCENDING,
              desc: PRIMARY_SORT_DESCENDING
            }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            sortRow;
            context: {
              which: 'secondary',
              label: 'TankProfiles.SortThen',
              ariaLabel: 'TankProfiles.SecondarySort',
              columnControl: 'secondaryColumnId',
              directionControl: 'secondaryColumnSortTypeId',
              asc: SECONDARY_SORT_ASCENDING,
              desc: SECONDARY_SORT_DESCENDING
            }
          "
        ></ng-container>
      </section>

      <section class="flex flex-col gap-3 border-x-0 border-y-0 border-b border-solid border-pill py-6">
        <h4 class="m-0 text-sm font-semibold">{{ 'TankProfiles.Scope' | translate }}</h4>
        <div class="flex items-center justify-between gap-4">
          <label for="tp-include-child-orgs-input" class="text-sm">{{
            'TankProfiles.IncludeChildOrganization' | translate
          }}</label>
          <mat-slide-toggle
            id="tp-include-child-orgs"
            color="primary"
            formControlName="includeChildOrganization"
            (change)="markDirty()"
          ></mat-slide-toggle>
        </div>
        <div class="flex items-center justify-between gap-4">
          <label for="tp-include-affiliated-input" class="text-sm">{{
            'TankProfiles.IncludeAffiliatedTanks' | translate
          }}</label>
          <mat-slide-toggle
            id="tp-include-affiliated"
            color="primary"
            formControlName="includeAffiliatedOrganization"
            (change)="markDirty()"
          ></mat-slide-toggle>
        </div>
      </section>

      <section class="flex flex-col gap-3 py-6">
        <h4 class="m-0 text-sm font-semibold">{{ 'TankProfiles.Output' | translate }}</h4>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-sm text-gray-600">{{ 'General.Visibility' | translate }}</span>
          <mat-button-toggle-group
            class="tl-segmented"
            formControlName="isPrivate"
            (change)="markDirty()"
            [attr.aria-label]="'General.Visibility' | translate"
          >
            <mat-button-toggle [value]="true">{{ 'General.Private' | translate }}</mat-button-toggle>
            <mat-button-toggle [value]="false">{{ 'General.Public' | translate }}</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-sm text-gray-600">{{ 'TankProfiles.PageLength' | translate }}</span>
          <mat-button-toggle-group
            class="tl-segmented"
            formControlName="pageLength"
            (change)="markDirty()"
            [attr.aria-label]="'TankProfiles.PageLength' | translate"
          >
            <mat-button-toggle *ngFor="let length of pageLengthOptions" [value]="length">{{
              length
            }}</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </section>
    </div>
  </div>

  <!-- One sort level: a searchable column picker plus its direction. The None option is hidden while
       searching so the first match is what Enter picks. -->
  <ng-template
    #sortRow
    let-which="which"
    let-label="label"
    let-ariaLabel="ariaLabel"
    let-columnControl="columnControl"
    let-directionControl="directionControl"
    let-asc="asc"
    let-desc="desc"
  >
    <div class="flex items-center gap-2">
      <span class="w-12 shrink-0 text-sm text-gray-600">{{ label | translate }}</span>
      <mat-form-field appearance="outline" class="tl-labelless-field tl-labelless-field--flush min-w-0 flex-1">
        <mat-select
          [formControlName]="columnControl"
          disableOptionCentering
          (selectionChange)="markDirty()"
          [aria-label]="ariaLabel | translate"
        >
          <app-select-search
            [placeholder]="'TankProfiles.FilterColumns' | translate"
            [noResultsText]="sortOptions[which].length ? '' : ('TankProfiles.NoColumnsMatchFilter' | translate)"
            (searchChange)="onSortSearch(which, $event)"
          ></app-select-search>
          <mat-option *ngIf="!sortSearch[which]" [value]="NO_SORT_COLUMN">{{
            'TankProfiles.NoSort' | translate
          }}</mat-option>
          <mat-option *ngFor="let column of sortOptions[which]; trackBy: trackColumn" [value]="column.columnID">{{
            columnLabel(column)
          }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-button-toggle-group class="tl-segmented shrink-0" [formControlName]="directionControl" (change)="markDirty()">
        <mat-button-toggle
          [value]="asc"
          [matTooltip]="'TankProfiles.Ascending' | translate"
          [attr.aria-label]="'TankProfiles.Ascending' | translate"
        >
          <mat-icon class="tl-icon-sm">arrow_upward</mat-icon>
        </mat-button-toggle>
        <mat-button-toggle
          [value]="desc"
          [matTooltip]="'TankProfiles.Descending' | translate"
          [attr.aria-label]="'TankProfiles.Descending' | translate"
        >
          <mat-icon class="tl-icon-sm">arrow_downward</mat-icon>
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>
  </ng-template>
</form>
